import { useEffect, useRef, useCallback, useState } from 'react';
import { useField, useFormikContext } from 'formik';
import { Input, InputTypes } from '@nextgen-web-framework/all';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, AppState } from '@nextgen-web-framework/core';
import { updateOtpData } from '@commerce-identity-webcomponents/sdk';
import styles from './OtpInputField.module.scss';
import { InputMode, OtpInputFieldProps } from './types/propTypes';

const OtpInputField = ({ autoverify = true, ...props }: OtpInputFieldProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const { submitForm, resetForm } = useFormikContext();
  const { otp, isChecking, isVerified, isInvalid } = useSelector((state: AppState) => state.commerceIdentity.otpData);
  const [field, , helpers] = useField(props);
  const { setValue } = helpers;
  const { value } = field;

  // Add this state to track user interaction
  const [userHasStartedTyping, setUserHasStartedTyping] = useState(false);

  const borderClass = isChecking ? 'checking' : isVerified ? 'verified' : isInvalid ? 'invalid' : 'no-outline';

  const otpKeys = ['input0', 'input1', 'input2', 'input3', 'input4', 'input5'];
  const inputRefs = useRef<Array<HTMLInputElement | null>>(Array(otpKeys.length).fill(null));

  useEffect(() => {
    if (!userHasStartedTyping) {
      const timer = setTimeout(() => {
        otpFocusInput();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [userHasStartedTyping]);

  useEffect(() => {
    if (isInvalid) {
      clearForm(true);
    }
  }, [isInvalid]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setUserHasStartedTyping(true);
      const { value = '', name } = e.target;
      dispatch(
        updateOtpData({
          otp: {
            ...otp,
            [name]: value,
          },
        }),
      );
      const nextIndex = Number(name[name.length - 1]) + 1;
      if (value.length && nextIndex < otpKeys.length) {
        otpFocusInput(nextIndex);
      }
    },
    [dispatch, otp],
  );

  const onKeyDownHandler = useCallback((e: React.KeyboardEvent, index: number) => {
    if (e.key === 'Backspace' && !(e.target as HTMLInputElement).value) {
      const prevIndex = index - 1 >= 0 ? index - 1 : 0;
      otpFocusInput(prevIndex);
    }
  }, []);

  const onPasteHandler = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      const pastedData = e.clipboardData.getData('Text');
      if (pastedData.length === otpKeys.length && /^\d+$/.test(pastedData)) {
        const updatedOtp: Record<string, string> = {};
        for (let i = 0; i < otpKeys.length; i++) {
          updatedOtp[otpKeys[i]] = pastedData[i];
        }
        dispatch(
          updateOtpData({
            otp: {
              ...otp,
              ...updatedOtp,
            },
          }),
        );
      }
    },
    [dispatch, otp],
  );

  const otpFocusInput = (index = 0) => {
    inputRefs.current[index]?.focus();
  };

  const clearForm = (clearOnlyOtpValues = false) => {
    setTimeout(() => {
      const otpEmptyState = {
        otp: {
          input0: '',
          input1: '',
          input2: '',
          input3: '',
          input4: '',
          input5: '',
        },
      };

      if (clearOnlyOtpValues) {
        resetForm({ values: { otp: '' } });
        dispatch(updateOtpData(otpEmptyState));
      } else {
        resetForm({ values: { otp: '' } });
        dispatch(
          updateOtpData({
            isVerified: false,
            isChecking: false,
            isInvalid: false,
            ...otpEmptyState,
          }),
        );
      }
      otpFocusInput(0);
    }, 0);
  };

  useEffect(() => {
    setValue(Object.values(otp).join(''));
  }, [otp, setValue]);

  useEffect(() => {
    if (value?.length === otpKeys.length) {
      autoverify && submitForm();
    }
  }, [value, autoverify, submitForm]);

  useEffect(
    () => () => {
      clearForm();
    },
    [dispatch, resetForm],
  );

  return (
    <div className={`${styles['identity-otp-field']} ${styles[borderClass]}`} data-testid="otp-input-field-test-id">
      {otpKeys.map((e, index) => (
        <Input
          key={e}
          data-testid={`verifyOtp-input${index + 1}-test-id`}
          name={e}
          className={styles['otp-inputs']}
          type={InputTypes.NUMBER}
          maxLength={1}
          onChange={handleInputChange}
          value={otp[e]}
          onKeyDown={(e) => onKeyDownHandler(e, index)}
          ref={(el) => (inputRefs.current[index] = el)}
          onPaste={onPasteHandler}
          inputMode={InputMode.NUMERIC}
          autoComplete="one-time-code"
        />
      ))}
    </div>
  );
};

export default OtpInputField;
